import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { BookOpen, Calendar, CheckCircle, XCircle, Clock } from "lucide-react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import config from "../../../config";

const UserBookSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = useSelector((s) => s.auth.user);
  const token = useSelector((s) => s.auth.token);

  useEffect(() => {
    fetchUserSuggestions();
  }, []);

  const fetchUserSuggestions = async () => {
    if (!user?.userId || !token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${config.url}/api/suggestions/user/${user.userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (data.responseCode === 200 && data.responseData) {
        setSuggestions(data.responseData);
      } else {
        console.error("Failed to fetch suggestions:", data.message);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      toast.error("Failed to load your book suggestions");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "APPROVED":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "REJECTED":
        return <XCircle className="w-5 h-5 text-red-600" />;
      case "PENDING":
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      APPROVED: "bg-green-100 text-green-800 border-green-300",
      REJECTED: "bg-red-100 text-red-800 border-red-300",
      PENDING: "bg-yellow-100 text-yellow-800 border-yellow-300",
    };

    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${
          styles[status] || styles.PENDING
        }`}
      >
        {getStatusIcon(status)}
        {status}
      </span>
    );
  };

  const formatDate = (dateArray) => {
    if (!dateArray || dateArray.length < 3) return "N/A";
    // dateArray format: [year, month, day, hour, minute, second]
    const [year, month, day] = dateArray;
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BookOpen className="text-primary" />
          My Book Suggestions
        </h3>
        <p className="text-gray-500 text-center py-8">Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <BookOpen className="text-primary" />
        My Book Suggestions
      </h3>

      {suggestions.length === 0 ? (
        <div className="text-center py-8">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">
            You haven't suggested any books yet.
          </p>
          <button
            onClick={() => window.location.href = "/suggest-book"}
            className="bg-primary text-white px-6 py-2 rounded-full hover:bg-primary-dark transition"
          >
            Suggest a Book
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {suggestions.map((suggestion, index) => (
            <motion.div
              key={suggestion.sugId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 text-lg">
                    {suggestion.title}
                  </h4>
                  <p className="text-gray-600 text-sm mt-1">
                    by {suggestion.author}
                  </p>
                  {suggestion.edition && (
                    <p className="text-gray-500 text-xs mt-1">
                      Edition: {suggestion.edition}
                    </p>
                  )}
                  {suggestion.bookLanguage && (
                    <p className="text-gray-500 text-xs">
                      Language: {suggestion.bookLanguage}
                    </p>
                  )}
                  {suggestion.message && (
                    <p className="text-gray-600 text-sm mt-2 italic">
                      "{suggestion.message}"
                    </p>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(suggestion.status)}
                  <div className="flex items-center gap-1 text-gray-500 text-xs">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(suggestion.creationDate)}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserBookSuggestions;
